/**
 * Templates: what the approved documents contain, read-only.
 *
 * Sales cannot edit any of this — it is what makes every proposal consistent.
 * Showing it here means a user can check what a template covers before choosing
 * it, without opening Word.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { endpoints, type TemplateManifest, type TemplateSummary } from '../lib/api';
import { useApiErrorHandler } from '../lib/auth';
import { Alert, Button, Card, Skeleton } from '../components/ui';

export function TemplatesPage() {
  const navigate = useNavigate();
  const handleError = useApiErrorHandler();

  const [templates, setTemplates] = useState<TemplateSummary[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [manifests, setManifests] = useState<Record<string, TemplateManifest>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    endpoints
      .templates()
      .then((result) => setTemplates(result.templates))
      .catch((caught) => setError(handleError(caught)));
  }, [handleError]);

  const toggle = async (templateId: string) => {
    if (openId === templateId) {
      setOpenId(null);
      return;
    }
    setOpenId(templateId);
    if (manifests[templateId]) return;
    try {
      const result = await endpoints.templateManifest(templateId);
      setManifests((current) => ({ ...current, [templateId]: result.manifest }));
    } catch (caught) {
      setError(handleError(caught));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-ink">Templates</h1>
          <p className="mt-0.5 text-[13px] text-muted">
            The five approved proposal families. Content is fixed; you configure the values
            around it.
          </p>
        </div>
        <Button onClick={() => navigate('/proposals/new')}>New Proposal</Button>
      </div>

      {error && <Alert tone="error">{error}</Alert>}

      {!templates ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {templates.map((template) => {
            const manifest = manifests[template.templateId];
            const isOpen = openId === template.templateId;
            return (
              <Card key={template.templateId} className="overflow-hidden">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <h2 className="text-sm font-semibold text-ink">
                      {template.name}
                    </h2>
                    <p className="mt-1 max-w-3xl text-[13px] text-body">
                      {template.description}
                    </p>
                    <p className="mt-1.5 text-[11px] uppercase tracking-wide text-muted">
                      Version {template.version} · {template.status}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => toggle(template.templateId)}
                      aria-expanded={isOpen}
                    >
                      {isOpen ? 'Hide contents' : 'View contents'}
                    </Button>
                  </div>
                </div>

                {isOpen && (
                  <div className="mt-5 border-t border-rule pt-5">
                    {!manifest ? (
                      <Skeleton className="h-32 w-full" />
                    ) : (
                      <div className="grid gap-6 md:grid-cols-2">
                        <Section title="Scope of work">
                          {manifest.scopeGroups.map((group) => (
                            <div key={group.id} className="mb-3 last:mb-0">
                              <p className="text-[12px] font-semibold uppercase tracking-wide text-teal">
                                {group.title}
                              </p>
                              <ul className="mt-1 space-y-0.5">
                                {group.items.map((item) => (
                                  <li key={item.id} className="text-[13px] text-body">
                                    · {item.text}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </Section>

                        <Section title="Deliverables">
                          <ul className="space-y-1.5">
                            {manifest.deliverables.map((deliverable) => (
                              <li key={deliverable.id} className="text-[13px]">
                                <span className="font-medium text-ink">
                                  {deliverable.name}
                                </span>
                                <span className="block text-muted">
                                  {deliverable.description}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </Section>

                        <Section title="Approach">
                          <ol className="space-y-1.5">
                            {manifest.approachPhases.map((phase) => (
                              <li key={phase.id} className="text-[13px]">
                                <span className="font-medium text-ink">
                                  {phase.number} · {phase.name}
                                </span>
                                <span className="block text-muted">
                                  {phase.description}
                                </span>
                              </li>
                            ))}
                          </ol>
                        </Section>

                        <Section title="Commercial line items">
                          <ul className="space-y-1">
                            {manifest.commercialItems.map((item) => (
                              <li key={item.id} className="text-[13px] text-body">
                                · {item.label}
                                {item.optional && (
                                  <span className="ml-1.5 rounded bg-tint px-1.5 py-0.5 text-[11px] text-muted">
                                    optional
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                          <p className="mt-2 text-[13px] font-medium text-ink">
                            {manifest.commercialTotalLabel}
                          </p>
                        </Section>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-2 text-[13px] font-semibold text-ink">{title}</h3>
      {children}
    </div>
  );
}
